/**
 * O convite para entrar na competição.
 *
 * No template: texto e lista de marcadores à esquerda, imagem grande à direita,
 * e uma forma recortada (`.community-union`) encaixando as duas metades. É a
 * seção mais editorial da página — não tem dado nenhum, só argumento.
 *
 * Onde o template tinha uma foto entram os canais oficiais — WhatsApp,
 * Discord e TikTok —, um card para cada, na cor de cada rede: é por lá que a
 * comunidade conversa, recebe os avisos e fica sabendo das edições novas. Uma
 * foto de banco de imagens ocuparia o mesmo lugar sem levar ninguém a lugar
 * nenhum.
 */

import { useRef } from 'react';
import { Link } from 'react-router';
import { useEntradaDaSecao } from './animacoes';
import { COMUNIDADE } from './conteudo';

const ICONES = { whatsapp: IconeWhatsApp, discord: IconeDiscord, tiktok: IconeTikTok };

export function SecaoComunidade() {
  const raiz = useRef<HTMLElement>(null);
  useEntradaDaSecao(raiz, '[data-entra]');

  return (
    <section className="community-section" ref={raiz}>
      <div className="container">
        <div className="community-wrapper">
          <div className="community-contents">
            <div className="community-top-detail">
              <h2 className="community-title" data-entra>
                {COMUNIDADE.titulo}
              </h2>
              <p className="large-paragraph community-para" data-entra>
                {COMUNIDADE.texto}
              </p>

              <div className="community-lists" data-entra>
                {COMUNIDADE.itens.map((item) => (
                  <div key={item} className="community-list">
                    <IconeDeCheck />
                    <div className="community-list-item">{item}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="community-buttons mpc-comunidade-botoes" data-entra>
              <Link to={COMUNIDADE.botao.destino} className="button-3">
                <div className="button-3-text">{COMUNIDADE.botao.rotulo}</div>
                <div className="button-3-bg" aria-hidden />
              </Link>
            </div>
          </div>

          {/* ---------------------------------------- os canais oficiais */}
          <div className="mpc-canais">
            {COMUNIDADE.canais.map((canal) => {
              const Icone = ICONES[canal.id];
              return (
                <aside
                  key={canal.id}
                  className={`mpc-canal mpc-canal-${canal.id}`}
                  data-entra
                  aria-label={canal.titulo}
                >
                  <span aria-hidden className="mpc-canal-marca">
                    <Icone />
                  </span>
                  <p className="mpc-canal-selo">
                    <span className="mpc-canal-ponto" aria-hidden />
                    Oficial
                  </p>
                  <h3 className="mpc-canal-titulo">{canal.titulo}</h3>
                  <p className="mpc-canal-texto">{canal.texto}</p>
                  <ul className="mpc-canal-itens">
                    {canal.itens.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a
                    href={canal.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mpc-canal-botao"
                  >
                    <Icone />
                    {canal.botao}
                  </a>
                  <p className="mpc-canal-rodape">{canal.rodape}</p>
                </aside>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Os ícones das redes, desenhados aqui para não depender de CDN de terceiro. */
function IconeWhatsApp() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="mpc-icone-canal">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.23 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28Z" />
    </svg>
  );
}

function IconeDiscord() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="mpc-icone-canal">
      <path d="M20.32 4.37a19.8 19.8 0 0 0-4.89-1.52.07.07 0 0 0-.08.04c-.21.38-.44.87-.61 1.25a18.27 18.27 0 0 0-5.49 0 12.6 12.6 0 0 0-.62-1.25.08.08 0 0 0-.08-.04 19.74 19.74 0 0 0-4.88 1.52.07.07 0 0 0-.03.03C.53 9.05-.32 13.58.1 18.06a.08.08 0 0 0 .03.06 19.9 19.9 0 0 0 5.99 3.03.08.08 0 0 0 .08-.03c.46-.63.87-1.3 1.23-1.99a.08.08 0 0 0-.04-.11 13.1 13.1 0 0 1-1.87-.89.08.08 0 0 1-.01-.13l.37-.29a.07.07 0 0 1 .08-.01c3.93 1.79 8.18 1.79 12.06 0a.07.07 0 0 1 .08.01l.37.29a.08.08 0 0 1-.01.13c-.6.35-1.22.65-1.87.89a.08.08 0 0 0-.04.11c.36.7.77 1.36 1.22 1.99a.08.08 0 0 0 .09.03 19.84 19.84 0 0 0 6-3.03.08.08 0 0 0 .03-.06c.5-5.18-.84-9.67-3.55-13.66a.06.06 0 0 0-.03-.03ZM8.02 15.33c-1.18 0-2.16-1.08-2.16-2.42 0-1.33.96-2.42 2.16-2.42 1.21 0 2.18 1.1 2.16 2.42 0 1.34-.96 2.42-2.16 2.42Zm7.97 0c-1.18 0-2.15-1.08-2.15-2.42 0-1.33.95-2.42 2.15-2.42 1.21 0 2.18 1.1 2.16 2.42 0 1.34-.95 2.42-2.16 2.42Z" />
    </svg>
  );
}

function IconeTikTok() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="mpc-icone-canal">
      <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.6 2.6 0 0 1-2.6-2.6 2.6 2.6 0 0 1 3.37-2.48V9.66a5.73 5.73 0 0 0-.77-.05A5.7 5.7 0 0 0 4.15 15.3 5.7 5.7 0 0 0 9.86 21a5.7 5.7 0 0 0 5.7-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.3 4.3 0 0 1-3.26-1.48Z" />
    </svg>
  );
}

/**
 * O marcador da lista.
 *
 * No template é uma imagem PNG de 4 KB baixada de um CDN, repetida quatro
 * vezes. Aqui é um SVG inline de meia dúzia de bytes que herda `currentColor` —
 * e que, por isso, acompanha o tema em vez de ficar com a cor em que foi salvo.
 */
function IconeDeCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="community-check-icon" aria-hidden>
      <circle cx="12" cy="12" r="10" fill="var(--_colors---text-color--bright-red)" />
      <path
        d="m8 12.2 2.7 2.7L16 9.6"
        stroke="var(--_colors---background-color--dark)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
